<template>
  <div class="admin-view">
    <NavigationView />
    <div class="page">
      <div class="page-header">
        <div>
          <div class="page-title">ปฏิทินการประชุม</div>
        </div>
        <v-btn color="secondary" depressed large class="cta-button" to="/createmeeting">
          <v-icon left>mdi-plus</v-icon>
          สร้างการประชุม
        </v-btn>
      </div>

      <v-card class="data-card">
        <div class="calendar-toolbar">
          <div class="calendar-nav">
            <v-btn icon @click="$refs.calendar.prev()"><v-icon>mdi-chevron-left</v-icon></v-btn>
            <v-btn icon @click="$refs.calendar.next()"><v-icon>mdi-chevron-right</v-icon></v-btn>
            <span class="calendar-title">{{ title }}</span>
            <v-btn outlined small color="primary" class="ml-2" @click="focus = ''">วันนี้</v-btn>
          </div>
          <v-btn-toggle v-model="type" mandatory dense color="primary">
            <v-btn value="month" small>เดือน</v-btn>
            <v-btn value="week" small>สัปดาห์</v-btn>
            <v-btn value="day" small>วัน</v-btn>
          </v-btn-toggle>
        </div>

        <v-progress-linear v-if="loading" indeterminate color="secondary"></v-progress-linear>

        <!-- มือถือ: ช่องวันแคบเกินจะอ่านชื่อ เลยแสดงเป็นแถบสี แตะวันเพื่อดูรายละเอียด -->
        <div v-if="compactMonth" class="calendar-hint">แตะวันที่เพื่อดูรายละเอียดการประชุม</div>

        <v-sheet :height="sheetHeight">
          <v-calendar
            ref="calendar"
            v-model="focus"
            :type="type"
            :events="events"
            :event-color="eventColor"
            :event-text-color="eventTextColor"
            :event-height="compactMonth ? 6 : 20"
            :weekday-format="weekdayFormat"
            :interval-format="intervalFormat"
            :first-interval="7"
            :interval-count="13"
            locale="th"
            event-overlap-mode="stack"
            :event-more-text="compactMonth ? '+{0}' : 'อีก {0} รายการ'"
            color="primary"
            @click:event="showEvent"
            @click:more="viewDay"
            @click:date="viewDay"
            @click:day="onDayClick"
            @change="onRangeChange"
          >
            <template v-slot:event="{ event, timed }">
              <div v-if="compactMonth"></div>
              <div v-else class="event-label" :class="{ 'event-label--mine': event.data.mine }">
                <span v-if="isLive(event)" class="live-dot" title="กำลังประชุม"></span>
                <span v-if="!timed || type === 'month'" class="event-time">{{ event.data.starttime }}</span>
                {{ event.name }}
              </div>
            </template>
          </v-calendar>

          <v-menu
            v-model="selectedOpen"
            :close-on-content-click="false"
            :activator="selectedElement"
            :offset-x="!isMobile"
            :offset-y="isMobile"
            max-width="380"
          >
            <v-card v-if="selected" class="event-card">
              <div class="event-card-bar" :class="{ 'event-card-bar--mine': selected.mine }"></div>
              <v-card-title class="event-card-title">{{ selected.title }}</v-card-title>
              <v-card-text class="event-card-body">
                <div class="event-row">
                  <v-icon small color="primary">mdi-calendar-clock</v-icon>
                  <span>{{ formatRange(selected) }}</span>
                </div>
                <div class="event-row">
                  <v-icon small color="primary">mdi-account-tie</v-icon>
                  <span>{{ selected.owner.name || '-' }}</span>
                </div>
                <div v-if="selected.mine" class="event-row">
                  <v-icon small color="primary">mdi-account-group</v-icon>
                  <span>ลงทะเบียนแล้ว {{ selected.registered }} คน</span>
                </div>
                <v-chip v-if="selected.mine" small color="secondary" text-color="primary" class="mt-2">การประชุมของฉัน</v-chip>
              </v-card-text>
              <v-card-actions v-if="selected.mine">
                <v-btn text color="primary" @click="openQr(selected)">
                  <v-icon left>mdi-qrcode</v-icon>QR Code
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn depressed color="primary" @click="manage(selected)">จัดการ</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </v-sheet>

        <div class="calendar-legend">
          <span><i class="dot dot--mine"></i>การประชุมของฉัน</span>
          <span><i class="dot dot--other"></i>การประชุมอื่น</span>
          <span><i class="dot dot--past"></i>การประชุมจบแล้ว</span>
        </div>
      </v-card>
    </div>

    <v-dialog v-model="qrOpen" max-width="500px">
      <v-card>
        <v-card-title class="headline dialog-title">QR Code ลงทะเบียน</v-card-title>
        <v-card-subtitle class="text-left">{{ qrTitle }}</v-card-subtitle>
        <v-card-text style="text-align: center;">
          <img :src="qrImage" width="200" height="200" alt="QR Code" />
          <qr-instructions :type="qrType"></qr-instructions>
          <v-btn color="primary" depressed @click="downloadQr">
            <v-icon left>mdi-download</v-icon>
            บันทึก QR Code
          </v-btn>
          <!-- โหมดฉายหน้าห้อง (ปิดไว้ก่อน เปิดใช้: เอา comment ออกทั้งปุ่ม แท็ก <present-qr> import และ components)
          <v-btn color="secondary" depressed class="cta-button ml-2" @click="presentOpen = true">
            <v-icon left>mdi-projector-screen</v-icon>
            ฉายหน้าห้อง
          </v-btn> -->
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="qrOpen = false">ปิด</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- <present-qr v-model="presentOpen" :link="qrLink" :title="qrTitle" :meeting-id="qrMeetingId"></present-qr> -->

    <FooterView></FooterView>
  </div>
</template>

<script>
import axios from 'axios';
import QRCode from 'qrcode';
import { API_URL } from '@/config';
import { fieldsOf, buildThaidLink } from '@/meetingFields';
import { downloadQrPoster } from '@/qrPoster';
import { qrHeadline } from '@/meetingType';
import NavigationView from '../components/Navigation.vue';
import FooterView from '../components/Footer.vue';
import QrInstructions from '../components/QrInstructions.vue';
// import PresentQr from '../components/PresentQr.vue';
import { settings, ensureSettings } from '@/fun/settings';
import { greetingFor, todaySummary } from '@/fun/greeting';
import { takeQueuedGreeting, showGreeting } from '@/fun/greetingToast';
import { mapGetters } from 'vuex';

const COLOR_MINE = '#FFC600';
const COLOR_OTHER = '#0C2F53';
const COLOR_PAST = '#CFD8DC';
const WEEKDAYS = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];

export default {
  name: 'DashboardView',
  components: { NavigationView, FooterView, QrInstructions /* , PresentQr */ },
  data() {
    return {
      settings,
      now: new Date(),
      nowTimer: null,
      myToday: null,
      presentOpen: false,
      qrMeetingId: null,
      focus: '',
      type: settings.calendarView || 'month',
      title: '',
      events: [],
      loading: false,
      selected: null,
      selectedElement: null,
      selectedOpen: false,
      qrOpen: false,
      qrTitle: '',
      qrType: '',
      qrLink: '',
      qrImage: '',
    };
  },
  computed: {
    ...mapGetters(['getUser']),
    firstName() {
      return (this.getUser && this.getUser.name) || '';
    },
    greeting() {
      return greetingFor(this.now, this.settings.greeting, this.firstName);
    },
    summary() {
      return todaySummary(this.myToday);
    },
    isMobile() {
      return this.$vuetify.breakpoint.xsOnly;
    },
    compactMonth() {
      return this.isMobile && this.type === 'month';
    },
    sheetHeight() {
      if (this.type !== 'month') return 760;
      return this.isMobile ? 480 : 720;
    },
  },
  mounted() {
    // โหลดการตั้งค่าของระบบก่อน (มุมมองปฏิทินเริ่มต้น / คำทักทาย)
    ensureSettings().then(() => {
      this.type = settings.calendarView || 'month';
      this.loadToday();
    });
    // อัปเดตเวลาทุกนาที: จุด "กำลังประชุม" สีของการประชุมที่จบแล้ว และคำทักทาย
    this.nowTimer = setInterval(() => { this.now = new Date(); }, 60000);
  },
  beforeDestroy() {
    clearInterval(this.nowTimer);
  },
  methods: {
    // จำนวนการประชุมของฉันวันนี้ (สำหรับคำทักทาย)
    async loadToday() {
      const d = new Date();
      const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      try {
        const res = await axios.get(`${API_URL}/calendar`, { params: { from: today, to: today } });
        this.myToday = (res.data.result || []).filter(m => m.mine).length;
      } catch (e) {
        this.myToday = null;
      }
      // ทักทายมุมขวาบนครั้งเดียวหลัง login (ปิดได้ในหน้าการตั้งค่า)
      if (takeQueuedGreeting() && this.settings.greeting.enabled) {
        showGreeting(this.greeting.text, this.myToday === null ? '' : this.summary);
      }
    },
    isPast(event) {
      return event.endAt < this.now;
    },
    isLive(event) {
      return event.timed && event.startAt <= this.now && this.now < event.endAt;
    },
    // ปฏิทินเปลี่ยนเดือน/สัปดาห์/วัน: โหลดการประชุมในช่วงที่แสดง
    async onRangeChange({ start, end }) {
      // หัวปฏิทินเป็นภาษาไทย ปี พ.ศ. (th-TH ใช้ปฏิทินพุทธโดยปริยาย)
      const opts = this.type === 'day'
        ? { day: 'numeric', month: 'long', year: 'numeric' }
        : { month: 'long', year: 'numeric' };
      this.title = new Intl.DateTimeFormat('th-TH', opts).format(new Date(`${start.date}T00:00:00`));
      this.loading = true;
      try {
        const res = await axios.get(`${API_URL}/calendar`, { params: { from: start.date, to: end.date } });
        this.events = (res.data.result || []).map(m => {
          const end = `${m.enddate} ${m.endtime || m.starttime || '23:59'}`;
          return {
            name: m.title,
            start: m.starttime ? `${m.startdate} ${m.starttime}` : m.startdate,
            end,
            timed: !!m.starttime,
            startAt: new Date(`${m.startdate}T${m.starttime || '00:00'}:00`),
            endAt: new Date(end.replace(' ', 'T') + ':00'),
            data: m,
          };
        });
      } catch (e) {
        console.error('Error loading calendar:', e.message);
        this.events = [];
      } finally {
        this.loading = false;
      }
    },
    eventColor(event) {
      if (this.isPast(event)) return COLOR_PAST;
      return event.data.mine ? COLOR_MINE : COLOR_OTHER;
    },
    eventTextColor(event) {
      return this.isPast(event) || event.data.mine ? COLOR_OTHER : '#FFFFFF';
    },
    weekdayFormat(day) {
      return WEEKDAYS[day.weekday];
    },
    intervalFormat(interval) {
      return interval.time;
    },
    viewDay({ date }) {
      this.focus = date;
      this.type = 'day';
    },
    // มือถือ: แตะตรงไหนของช่องวันก็เปิดมุมมองรายวัน
    onDayClick(day) {
      if (this.compactMonth) this.viewDay(day);
    },
    showEvent({ nativeEvent, event, day }) {
      if (this.compactMonth) {
        nativeEvent.stopPropagation();
        this.viewDay(day && day.date ? day : { date: event.data.startdate });
        return;
      }
      const open = () => {
        this.selected = event.data;
        this.selectedElement = nativeEvent.target;
        requestAnimationFrame(() => requestAnimationFrame(() => { this.selectedOpen = true; }));
      };
      if (this.selectedOpen) {
        this.selectedOpen = false;
        requestAnimationFrame(() => requestAnimationFrame(open));
      } else {
        open();
      }
      nativeEvent.stopPropagation();
    },
    formatRange(m) {
      const date = d => new Intl.DateTimeFormat('th-TH', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
        .format(new Date(`${d}T00:00:00`));
      const time = `${m.starttime || ''}${m.endtime ? `–${m.endtime}` : ''}`;
      if (m.enddate && m.enddate !== m.startdate) {
        return `${date(m.startdate)} ${m.starttime || ''} – ${date(m.enddate)} ${m.endtime || ''}`;
      }
      return `${date(m.startdate)} ${time} น.`;
    },
    // ไปหน้ารายการประชุมพร้อมค้นหาการประชุมนี้ให้ (แก้ไข / รายชื่อ / export อยู่ที่หน้านั้น)
    manage(m) {
      this.$router.push({ path: '/listmeeting', query: { search: m.title } });
    },
    openQr(m) {
      this.qrTitle = m.title;
      this.qrType = m.type;
      this.qrMeetingId = m.id;
      this.qrLink = buildThaidLink(m.id, fieldsOf(m));
      this.qrImage = '';
      this.qrOpen = true;
      QRCode.toDataURL(this.qrLink, (err, url) => {
        if (err) {
          console.error(err);
          return;
        }
        this.qrImage = url;
      });
    },
    downloadQr() {
      downloadQrPoster(this.qrLink, this.qrTitle, qrHeadline(this.qrType));
    },
  },
};
</script>

<style scoped>
.calendar-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #E8EDF3;
}

.calendar-nav {
  display: flex;
  align-items: center;
}

.calendar-title {
  color: #0C2F53;
  font-size: 20px;
  font-weight: 700;
  margin-left: 4px;
}

.event-label {
  padding: 0 6px;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-label--mine {
  font-weight: 600;
}

.event-time {
  font-weight: 600;
  margin-right: 4px;
}

.event-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.event-card-bar {
  height: 6px;
  background-color: #0C2F53;
}

.event-card-bar--mine {
  background-color: #FFC600;
}

.event-card-title {
  color: #0C2F53;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.4;
  word-break: break-word;
}

.event-card-body {
  font-size: 15px;
}

.event-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  color: #2B3A4D;
}

.calendar-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 12px 16px;
  border-top: 1px solid #E8EDF3;
  color: #5B6B7F;
  font-size: 14px;
}

.dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  margin-right: 6px;
  vertical-align: -1px;
}

.calendar-hint {
  padding: 8px 16px;
  color: #5B6B7F;
  font-size: 13px;
  border-bottom: 1px solid #E8EDF3;
}

@media (max-width: 599px) {
  .calendar-toolbar {
    padding: 8px 8px 12px;
  }

  .calendar-title {
    font-size: 18px;
  }

  .calendar-legend {
    gap: 8px 16px;
    font-size: 13px;
  }
}

.dot--mine { background-color: #FFC600; }
.dot--other { background-color: #0C2F53; }
.dot--past { background-color: #CFD8DC; }
</style>
